import FileSaver from 'file-saver'
import XLSX from 'xlsx'
import {
  get_user_info,
  get_user_conn_records,
  get_user_disc_conn_records,
  get_user_sub_records,
  get_user_api_event_records,
} from './requests.js'

const USER_INFO_FIELDS = [
  ['email', '邮箱'], ['uid', 'UID'], ['lastLogin', '上次登录时间'],
  ['createTime', '注册时间'], ['campaign_id', '广告系列'], ['site_id', '搜索词'],
  ['install_time', '安装时间'], ['shop_country', '商店页地区'], ['mcc', '运营商地区'],
  ['vip_desc', '会员信息'], ['sub_platform', '订阅平台'], ['sub_status', '订阅状态'],
  ['expireTime', '订阅过期时间'], ['real_expireTime', '会员过期时间'], ['device_num', '设备数'],
]
const DEVICE_FIELDS = [
  ['deviceId', '设备ID'], ['uid', 'UID'], ['email', '设备邮箱'], ['api', 'API版本'],
  ['deviceName', '设备名称'], ['brand', '品牌'], ['mcc', '运营商'], ['operation', '系统'],
  ['country', '国家'], ['lang', '语言'], ['model', '型号'], ['version', 'App版本'],
  ['ip', 'IP地址'], ['createTime', '创建时间'], ['lastLogin', '最后登录时间'],
]

const RECORD_FIELDS = {
  '连接记录': [
    ['sid', 'Sid'], ['occur_time', '连接日期'], ['ip', 'IP地址'], 
    ['dev', '设备ID'], ['v', '版本号'], ['mcc', '运营商'], ['os', '操作系统'],
    ['o', 'API版本'], ['c', '国家'], ['l', '语言'], ['m', '型号'], ['sc', '商店页'],
    ['auto', '连接模式'], ['vid', '服务器分组'], ['brand', '厂商'], ['cid', '城市'],
    ['cost', '连接耗时'], ['success', '连接结果'], ['cc', '连接次数'], ['ts', '服务器时间'],
    ['is_error', '时间异常'], ['group', '服务器分组'],
  ],
  '断连记录': [
    ['sid', 'Sid'], ['occur_time', '连接日期'], ['dev', '设备ID'], 
    ['auto', '连接模式'], ['vid', '服务器分组'], ['cid', '城市'], ['brand', '厂商'],
    ['block', '广告屏蔽'], ['bcount', '广告屏蔽次数'], ['group', '服务器分组'],
    ['tx', '上传流量'], ['rx', '下载流量'], ['times', '连接时长'], ['os', '平台'],
    ['m', '型号'], ['o', 'API版本'], ['v', '版本号'], ['mcc', '运营商'], ['c', '国家'], ['l', '语言'],
  ],
  '订阅记录': [
    ['productId', '订阅套餐'], ['startTime', '开始时间'], ['expireTime', '结束时间'],
    ['currencyCode', '支付货币'], ['priceAmount', '支付金额'], ['isRefund', '退款状态'],
    ['subPlatform', '订阅平台'], ['paymentEmail', '支付邮箱'], ['offer_type', '推介优惠'],
    ['createTime', '创建时间'], ['updateTime', '更新时间'], ['subId', '订阅ID'],
  ],
  '设备列表': DEVICE_FIELDS,
  'API记录': [
    ['event_code', '操作事件'], ['device_id', '设备ID'], ['channel', '渠道'], ['sub_channel', '子渠道'],
    ['version', '版本号'], ['platform', '客户端'], ['mcc', '运营商'], ['ip', 'IP地址'],
    ['package', '包名'], ['site_id', '搜索词'], ['campaign_id', '广告系列'], ['country', '国家'],
    ['city', '城市'], ['ts', '操作时间'],
  ],
}

function toSafeCellValue(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  const text = String(value)
  // Excel treats these prefixes as formulas; keep exported user data inert.
  return /^[=+\-@]/.test(text) ? `'${text}` : value
}

function extendFields(fields, records) {
  const known = new Set(fields.map(([key]) => key))
  const extra = []
  records.forEach(record => {
    Object.keys(record || {}).forEach(key => {
      if (!known.has(key)) {
        known.add(key)
        extra.push([key, key])
      }
    })
  })
  return fields.concat(extra)
}

function createSheet(fields, records) {
  const rows = [fields.map(([, label]) => label)]
  records.forEach(record => {
    rows.push(fields.map(([key]) => toSafeCellValue(record && record[key])))
  })
  const worksheet = XLSX.utils.aoa_to_sheet(rows)
  worksheet['!cols'] = fields.map(([, label]) => ({
    wch: Math.min(Math.max(String(label).length + 2, 12), 30),
  }))
  return worksheet
}

function assertSuccess(response, section) {
  const payload = response && response.data ? response.data : {}
  if (payload.code !== 200) {
    throw new Error(`${section}获取失败：${payload.msg || '未知错误'}`)
  }
  return payload
}
function stripKey(record, key) {
  if (!record || typeof record !== 'object') return record
  const copy = { ...record }
  delete copy[key]
  return copy
}

function buildWorkbook(userInfoPayload, connPayload, discPayload, subPayload, apiPayload) {
  const workbook = XLSX.utils.book_new()
  const userInfo = userInfoPayload.userInfo || {}
  const deviceList = Array.isArray(userInfo.deviceList) ? userInfo.deviceList : []
  const basicUserInfo = Object.assign({}, userInfo)
  delete basicUserInfo.deviceList
  const userInfoFields = extendFields(USER_INFO_FIELDS, [basicUserInfo])
  const userInfoRecords = userInfoFields.map(([key, label]) => ({ field: label, value: basicUserInfo[key] }))
  XLSX.utils.book_append_sheet(
    workbook,
    createSheet([['field', '字段'], ['value', '值']], userInfoRecords),
    '基础信息',
  )

  const sections = [
    ['连接记录', (connPayload.records || []).filter(record => record && record['success'] === '成功').map(record => stripKey(record, 'p'))],
    ['断连记录', (discPayload.records || []).map(record => stripKey(record, 'p'))],
    ['订阅记录', subPayload.records || []],
    ['设备列表', deviceList],
    ['API记录', apiPayload.records || []],
  ]
  sections.forEach(([sheetName, records]) => {
    const fields = extendFields(RECORD_FIELDS[sheetName], records)
    XLSX.utils.book_append_sheet(workbook, createSheet(fields, records), sheetName)
  })
  return workbook
}

/** 查询指定用户的五类资料并下载为一个 Excel 文件。 */
export async function export_user_info_excel(email, p = 'MetroVPN') {
  const normalizedEmail = String(email || '').trim()
  if (!normalizedEmail) throw new Error('邮箱不能为空')

  const params = { email: normalizedEmail, p }
  const [userResponse, connResponse, discResponse, subResponse, apiResponse] = await Promise.all([
    get_user_info(params),
    get_user_conn_records(params),
    get_user_disc_conn_records(params),
    get_user_sub_records(params),
    get_user_api_event_records(params),
  ])

  const userInfoPayload = assertSuccess(userResponse, '用户信息')
  const connPayload = assertSuccess(connResponse, '连接记录')
  const discPayload = assertSuccess(discResponse, '断连记录')
  const subPayload = assertSuccess(subResponse, '订阅记录')
  const apiPayload = assertSuccess(apiResponse, 'API记录')
  const workbook = buildWorkbook(userInfoPayload, connPayload, discPayload, subPayload, apiPayload)
  const filename = `user_${normalizedEmail.replace(/[^a-zA-Z0-9._-]/g, '_')}.xlsx`
  const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  FileSaver.saveAs(
    new Blob([output], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename,
  )
  return { filename, sheetNames: workbook.SheetNames }
}

export { buildWorkbook }
